// ============================================================
// FichaNucleo.js — a ficha de cadastro de núcleo, preenchida por link
//
// Página PÚBLICA, fora do login. Abre em /ficha?f=<ficha>&t=<token> e é
// montada antes da tela de entrada, no App.js.
//
// Sem login porque quem preenche a parte técnica é o coordenador técnico
// de um núcleo que ainda não existe, e por isso ainda não tem conta. O
// token do link é a credencial, e é ele que decide qual das duas partes
// abre: a parte NUNCA vem do endereço, senão trocar "geral" por "tecnica"
// na barra do navegador abriria o formulário do outro.
//
// A tela não sabe e não decide nada sobre permissão: ela mostra o que
// abrirFichaDeNucleo devolveu e manda de volta o que foi digitado. Limite
// de campo, coordenada válida e grafia de categoria são conferidos no
// servidor (functions/fichas.js, com teste).
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { abrirFichaDeNucleo, enviarFichaDeNucleo } from "../firebase";
import {
  AZUL, TEXTO, CINZA_ESC, FONTE, FUNDO_SUAVE, VERDE_LOGO, PERIGO, ALERTA, BORDA, RAIO,
} from "../tema";
import { Card, Btn, Input, StatusBox } from "../componentes";

// Quebra de linha escrita sem barra invertida, que some em algumas
// ferramentas de edição e deixa a regex partida no meio.
const QUEBRA_DE_LINHA = String.fromCharCode(10);
const SEPARADOR_DE_LINHA = new RegExp(String.fromCharCode(13) + "?" + String.fromCharCode(10));

const DIACRITICOS = new RegExp(
  "[" + String.fromCharCode(0x300) + "-" + String.fromCharCode(0x36f) + "]",
  "g"
);

// A mesma chave que o servidor usa para casar modalidade com categoria. O
// cadastro tem "BMX" ao lado de "Bmx", e por texto puro a categoria de uma
// grafia não valeria para a outra.
function chaveDaModalidade(m) {
  return String(m || "").normalize("NFD").replace(DIACRITICOS, "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function textoParaLista(texto) {
  return String(texto || "").split(",").map((m) => m.trim()).filter(Boolean);
}

function linhasParaLista(texto) {
  return String(texto || "").split(SEPARADOR_DE_LINHA).map((c) => c.trim()).filter(Boolean);
}

// O endereço da ficha. Lê da query (?f=&t=) para o link poder ser colado em
// qualquer mensagem sem depender de roteador.
export function dadosDoLinkDaFicha(busca) {
  const params = new URLSearchParams(busca || "");
  return { fichaId: params.get("f") || "", token: params.get("t") || "" };
}

// Um par de coordenadas colado do Google Maps. Quem preenche não digita
// latitude e longitude em dois campos: clica no ponto, copia "-3.7436,
// -38.5361" e cola. Aceitar só dois campos numéricos garantiria o par
// trocado, que põe a pista do Ceará no meio do oceano.
export function lerPonto(texto) {
  const t = String(texto || "").trim();
  if (!t) return null;
  const partes = t.replace(/[()]/g, "").split(/[,;\s]+/).filter(Boolean);
  if (partes.length < 2) return null;
  const lat = Number(partes[0].replace(",", "."));
  const lng = Number(partes[1].replace(",", "."));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

const ESTILO_AREA = {
  width: "100%", boxSizing: "border-box", resize: "vertical",
  border: "1px solid #D1D5DB", borderRadius: 8, padding: "9px 11px",
  fontSize: 13.5, fontFamily: "inherit", lineHeight: 1.6, background: "#FFF",
};

function Area({ label, ajuda, value, onChange, rows = 4, placeholder }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 600, color: CINZA_ESC }}>{label}</label>}
      {ajuda && <div style={{ fontSize: 12, color: CINZA_ESC, lineHeight: 1.55 }}>{ajuda}</div>}
      <textarea
        value={value} onChange={(e) => onChange(e.target.value)}
        rows={rows} placeholder={placeholder} style={ESTILO_AREA}
      />
    </div>
  );
}

function Bloco({ titulo, descricao, children }) {
  return (
    <div style={{ borderTop: `1px solid ${BORDA}`, paddingTop: 18, marginTop: 4 }}>
      <div style={{ fontSize: 14.5, fontWeight: 800, color: TEXTO }}>{titulo}</div>
      {descricao && (
        <div style={{ fontSize: 12.5, color: CINZA_ESC, lineHeight: 1.6, marginTop: 4, marginBottom: 14 }}>
          {descricao}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>
    </div>
  );
}

// ── Parte da coordenação geral ──────────────────────────────

function ParteGeral({ dados, onMudar }) {
  const v = (campo) => dados[campo] || "";
  return (
    <>
      <Bloco
        titulo="O núcleo"
        descricao="O nome por extenso é o que vai aparecer nos relatórios e no termo que o responsável assina. O código interno quem monta é a administração do programa, a partir do estado e da sigla."
      >
        <Input label="Nome do núcleo por extenso" value={v("nomeDoNucleo")} onChange={(x) => onMudar("nomeDoNucleo", x)}
          placeholder="Ex: Centro de Formação Olímpica do Ceará" />
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Input label="Estado" value={v("estado")} onChange={(x) => onMudar("estado", x)}
            placeholder="Ex: Ceará" style={{ flex: 1, minWidth: 180 }} />
          <Input label="Sigla da instituição (se houver)" value={v("siglaInstituicao")} onChange={(x) => onMudar("siglaInstituicao", x)}
            placeholder="Ex: CFO, IFCE" style={{ flex: 1, minWidth: 180 }} />
        </div>
      </Bloco>

      <Bloco
        titulo="Quem responde pelos dados dos atletas"
        descricao="As duas informações abaixo saem impressas no termo de consentimento que o responsável pelo atleta assina. Enquanto elas não chegarem, o termo não é emitido."
      >
        <Input label="Instituição responsável pelo programa no núcleo" value={v("instituicao")} onChange={(x) => onMudar("instituicao", x)}
          placeholder="Ex: Instituto Federal de Educação, Ciência e Tecnologia do Ceará" />
        <Input label="Canal para o responsável exercer seus direitos" value={v("canalDeDireitos")} onChange={(x) => onMudar("canalDeDireitos", x)}
          placeholder="Ex: revelartalentos@ifce.edu.br" />
        <div style={{ fontSize: 12, color: CINZA_ESC, lineHeight: 1.6 }}>
          É o e-mail ou o telefone institucional pelo qual o responsável pede acesso,
          correção ou exclusão dos dados do atleta. Precisa ser um canal que alguém
          acompanhe, não o contato pessoal de quem preenche esta ficha.
        </div>
      </Bloco>

      <Bloco
        titulo="Coordenação do núcleo"
        descricao="O e-mail é o login da pessoa no sistema, então precisa ser de uso real dela e não pode repetir o de outra conta."
      >
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Input label="Coordenador do núcleo" value={v("coordenadorNome")} onChange={(x) => onMudar("coordenadorNome", x)}
            placeholder="Nome completo" style={{ flex: 1, minWidth: 220 }} />
          <Input label="E-mail do coordenador" value={v("coordenadorEmail")} onChange={(x) => onMudar("coordenadorEmail", x)}
            type="email" placeholder="nome@instituicao.br" style={{ flex: 1, minWidth: 220 }} />
        </div>
        <Input label="Telefone do coordenador (opcional)" value={v("coordenadorTelefone")} onChange={(x) => onMudar("coordenadorTelefone", x)}
          placeholder="Com DDD" />
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Input label="Coordenador técnico" value={v("coordenadorTecnicoNome")} onChange={(x) => onMudar("coordenadorTecnicoNome", x)}
            placeholder="Nome completo" style={{ flex: 1, minWidth: 220 }} />
          <Input label="E-mail do coordenador técnico" value={v("coordenadorTecnicoEmail")} onChange={(x) => onMudar("coordenadorTecnicoEmail", x)}
            type="email" placeholder="nome@instituicao.br" style={{ flex: 1, minWidth: 220 }} />
        </div>
      </Bloco>

      <Bloco
        titulo="Modalidades"
        descricao="As modalidades que o núcleo vai atender. É esta lista que define as caixas de categoria que a coordenação técnica preenche na outra parte da ficha."
      >
        <Input label="Modalidades (separadas por vírgula)" value={v("modalidadesTexto")} onChange={(x) => onMudar("modalidadesTexto", x)}
          placeholder="Ex: BMX, Skate, Tênis de Mesa" />
      </Bloco>
    </>
  );
}

// ── Parte da coordenação técnica ────────────────────────────

function ParteTecnica({ dados, onMudar, modalidadesInformadas }) {
  const v = (campo) => dados[campo] || "";

  // As modalidades vêm da parte geral quando ela já chegou. Quando não
  // chegou, quem preenche informa, porque as duas partes podem chegar em
  // qualquer ordem e ficar sem caixa de categoria seria perder a visita.
  const modalidades = modalidadesInformadas.length
    ? modalidadesInformadas
    : textoParaLista(v("modalidadesTexto"));

  const locais = Array.isArray(dados.locais) ? dados.locais : [];
  const equipe = Array.isArray(dados.equipe) ? dados.equipe : [];

  function mudarItem(campo, indice, chave, valor) {
    const lista = Array.isArray(dados[campo]) ? dados[campo] : [];
    onMudar(campo, lista.map((item, i) => (i === indice ? { ...item, [chave]: valor } : item)));
  }

  function adicionar(campo, vazio) {
    const lista = Array.isArray(dados[campo]) ? dados[campo] : [];
    onMudar(campo, [...lista, vazio]);
  }

  function remover(campo, indice) {
    const lista = Array.isArray(dados[campo]) ? dados[campo] : [];
    onMudar(campo, lista.filter((_, i) => i !== indice));
  }

  return (
    <>
      {modalidadesInformadas.length === 0 && (
        <Bloco
          titulo="Modalidades"
          descricao="A coordenação geral ainda não informou as modalidades deste núcleo. Escreva as que você vai atender, e as caixas de categoria aparecem abaixo."
        >
          <Input label="Modalidades (separadas por vírgula)" value={v("modalidadesTexto")} onChange={(x) => onMudar("modalidadesTexto", x)}
            placeholder="Ex: BMX, Skate, Tênis de Mesa" />
        </Bloco>
      )}

      {modalidadesInformadas.length > 0 && (
        <Bloco titulo="Modalidades do núcleo" descricao="Informadas pela coordenação geral. Se faltar alguma, escreva nas observações ao final.">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {modalidadesInformadas.map((m) => (
              <span key={m} style={{
                background: "#EAF0FA", color: AZUL, borderRadius: 999,
                padding: "5px 12px", fontSize: 12.5, fontWeight: 700,
              }}>{m}</span>
            ))}
          </div>
        </Bloco>
      )}

      <Bloco
        titulo="Categorias de cada modalidade"
        descricao="Uma categoria por linha, na ordem em que as provas correm. Elas viram a lista de escolha no cadastro do atleta e no lançamento de resultado. Escreva sempre do mesmo jeito, porque Boys 13 anos e Boys - 13 contam como duas categorias diferentes no quadro de medalhas."
      >
        {modalidades.length === 0 && (
          <div style={{ fontSize: 13, color: CINZA_ESC }}>
            Informe as modalidades acima para abrir as caixas de categoria.
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
          {modalidades.map((m) => {
            const k = chaveDaModalidade(m);
            const linhas = linhasParaLista((dados.categorias || {})[k]);
            return (
              <div key={k} style={{ background: FUNDO_SUAVE, border: `1px solid ${BORDA}`, borderRadius: 9, padding: "10px 12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{m}</span>
                  <span style={{ fontSize: 11.5, color: CINZA_ESC }}>
                    {linhas.length === 0 ? "sem categorias" : `${linhas.length} categoria(s)`}
                  </span>
                </div>
                <textarea
                  value={(dados.categorias || {})[k] || ""}
                  onChange={(e) => onMudar("categorias", { ...(dados.categorias || {}), [k]: e.target.value })}
                  rows={Math.min(12, Math.max(4, linhas.length + 1))}
                  placeholder={["Girls 12 anos", "Boys 13 anos", "Elite Men"].join(QUEBRA_DE_LINHA)}
                  style={ESTILO_AREA}
                />
                <div style={{ marginTop: 8 }}>
                  <Input
                    label="Quantos sobem ao pódio nesta modalidade"
                    value={(dados.podios || {})[k] || ""}
                    onChange={(x) => onMudar("podios", { ...(dados.podios || {}), [k]: x })}
                    placeholder="3"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Bloco>

      <Bloco
        titulo="Locais oficiais de treino"
        descricao="Pode ser mais de um, até dez. Cadastre todos os lugares onde o treino acontece de forma permanente: a presença registrada longe de qualquer um deles entra como irregular. O limite é de 300 metros em volta de cada ponto, e não é configurável. Instalação grande, como um campus ou um clube, se cobre com mais pontos, não com um raio maior."
      >
        {locais.map((l, i) => {
          const ponto = lerPonto(l.ponto);
          return (
            <div key={i} style={{ background: FUNDO_SUAVE, border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: CINZA_ESC }}>Local {i + 1}</span>
                <Btn variant="discreto" tamanho="sm" onClick={() => remover("locais", i)}>Remover</Btn>
              </div>
              <Input label="Nome do local" value={l.nome || ""} onChange={(x) => mudarItem("locais", i, "nome", x)}
                placeholder="Ex: Pista de BMX do IFCE" />
              <Input label="Endereço" value={l.endereco || ""} onChange={(x) => mudarItem("locais", i, "endereco", x)}
                placeholder="Ex: Av. Treze de Maio, 2081, Benfica, Fortaleza" />
              <Input label="Ponto no mapa" value={l.ponto || ""} onChange={(x) => mudarItem("locais", i, "ponto", x)}
                placeholder="-3.7436, -38.5361" />
              <div style={{ fontSize: 12, color: ponto ? VERDE_LOGO : CINZA_ESC, lineHeight: 1.6 }}>
                {ponto
                  ? `Entendido: latitude ${ponto.lat}, longitude ${ponto.lng}.`
                  : "Abra o Google Maps, toque e segure no ponto exato do treino, copie as coordenadas que aparecem e cole aqui. Sem o ponto, o local fica cadastrado sem conferência de distância."}
              </div>
            </div>
          );
        })}
        <div>
          <Btn variant="secondary" onClick={() => adicionar("locais", { nome: "", endereco: "", ponto: "" })}
            disabled={locais.length >= 10}>
            {locais.length === 0 ? "Adicionar o local de treino" : "Adicionar outro local"}
          </Btn>
        </div>
      </Bloco>

      <Bloco
        titulo="Equipe do núcleo"
        descricao="Quem vai trabalhar no núcleo. Nenhuma conta é criada por esta ficha: a administração do programa cadastra essas pessoas depois, e o e-mail informado vira o login de cada uma."
      >
        {equipe.map((p, i) => (
          <div key={i} style={{ background: FUNDO_SUAVE, border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12.5, fontWeight: 800, color: CINZA_ESC }}>Pessoa {i + 1}</span>
              <Btn variant="discreto" tamanho="sm" onClick={() => remover("equipe", i)}>Remover</Btn>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Input label="Nome completo" value={p.nome || ""} onChange={(x) => mudarItem("equipe", i, "nome", x)}
                style={{ flex: 1, minWidth: 200 }} />
              <Input label="E-mail" value={p.email || ""} onChange={(x) => mudarItem("equipe", i, "email", x)}
                type="email" style={{ flex: 1, minWidth: 200 }} />
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Input label="Função" value={p.perfil || ""} onChange={(x) => mudarItem("equipe", i, "perfil", x)}
                placeholder="Ex: Técnico, Preparador Físico, Monitor" style={{ flex: 1, minWidth: 200 }} />
              <Input label="Modalidade (para técnico e auxiliar)" value={p.modalidade || ""} onChange={(x) => mudarItem("equipe", i, "modalidade", x)}
                placeholder="Ex: BMX" style={{ flex: 1, minWidth: 200 }} />
            </div>
          </div>
        ))}
        <div>
          <Btn variant="secondary" onClick={() => adicionar("equipe", { nome: "", email: "", perfil: "", modalidade: "" })}
            disabled={equipe.length >= 30}>
            {equipe.length === 0 ? "Adicionar a primeira pessoa" : "Adicionar outra pessoa"}
          </Btn>
        </div>
      </Bloco>
    </>
  );
}

// ── A página ────────────────────────────────────────────────

const ROTULO_DA_PARTE = {
  geral: "Coordenação geral",
  tecnica: "Coordenação técnica",
};

const EXPLICACAO_DA_PARTE = {
  geral: "Esta parte da ficha é da coordenação geral: o convênio, a instituição que responde pelos dados e quem vai coordenar o núcleo.",
  tecnica: "Esta parte da ficha é da coordenação técnica: as categorias de cada modalidade, os locais de treino e a equipe.",
};

export default function FichaNucleo({ fichaId, token }) {
  const [carregando, setCarregando] = useState(true);
  const [erroDeAbertura, setErroDeAbertura] = useState(null);
  const [ficha, setFicha] = useState(null);
  const [dados, setDados] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [status, setStatus] = useState(null);
  const [enviada, setEnviada] = useState(false);

  // Traz o que já foi enviado antes, para quem reabre o link corrigir em
  // vez de digitar tudo de novo. As listas e os mapas voltam ao formato de
  // edição da tela: categoria é texto com uma por linha, e o ponto do mapa
  // é o par colado.
  const carregar = useCallback(async () => {
    setCarregando(true);
    setErroDeAbertura(null);
    try {
      const r = await abrirFichaDeNucleo(fichaId, token);
      const f = r.ficha;
      setFicha(f);
      const d = f.dados || {};
      if (f.parte === "geral") {
        setDados({
          ...d,
          modalidadesTexto: (d.modalidades || []).join(", "),
        });
      } else {
        const categorias = {};
        Object.entries(d.categoriasPorModalidade || {}).forEach(([k, lista]) => {
          categorias[k] = (lista || []).join(QUEBRA_DE_LINHA);
        });
        const podios = {};
        Object.entries(d.podioPorModalidade || {}).forEach(([k, n]) => {
          podios[k] = String(n);
        });
        setDados({
          ...d,
          modalidadesTexto: (d.modalidades || []).join(", "),
          categorias,
          podios,
          locais: (d.locais || []).map((l) => ({
            nome: l.nome || "",
            endereco: l.endereco || "",
            ponto: Number.isFinite(l.lat) && Number.isFinite(l.lng) ? `${l.lat}, ${l.lng}` : "",
          })),
          equipe: d.equipe || [],
        });
      }
    } catch (e) {
      setErroDeAbertura(e.message || "Não foi possível abrir o formulário.");
    } finally {
      setCarregando(false);
    }
  }, [fichaId, token]);

  useEffect(() => { carregar(); }, [carregar]);

  function mudar(campo, valor) {
    setDados((atual) => ({ ...atual, [campo]: valor }));
    setStatus(null);
  }

  async function enviar() {
    setEnviando(true);
    setStatus(null);
    try {
      const envio = {
        quemPreencheuNome: dados.quemPreencheuNome || "",
        quemPreencheuEmail: dados.quemPreencheuEmail || "",
        observacoes: dados.observacoes || "",
      };

      if (ficha.parte === "geral") {
        Object.assign(envio, {
          nomeDoNucleo: dados.nomeDoNucleo || "",
          estado: dados.estado || "",
          siglaInstituicao: dados.siglaInstituicao || "",
          instituicao: dados.instituicao || "",
          canalDeDireitos: dados.canalDeDireitos || "",
          coordenadorNome: dados.coordenadorNome || "",
          coordenadorEmail: dados.coordenadorEmail || "",
          coordenadorTelefone: dados.coordenadorTelefone || "",
          coordenadorTecnicoNome: dados.coordenadorTecnicoNome || "",
          coordenadorTecnicoEmail: dados.coordenadorTecnicoEmail || "",
          modalidades: textoParaLista(dados.modalidadesTexto),
        });
      } else {
        const categorias = {};
        Object.entries(dados.categorias || {}).forEach(([k, texto]) => {
          categorias[k] = linhasParaLista(texto);
        });
        // O ponto colado vira latitude e longitude só aqui, na saída. O
        // local sem ponto segue mesmo assim: ele fica na ficha, e a
        // administração vê que falta a coordenada.
        const locais = (dados.locais || []).map((l) => {
          const ponto = lerPonto(l.ponto);
          return {
            nome: l.nome || "",
            endereco: l.endereco || "",
            lat: ponto ? ponto.lat : null,
            lng: ponto ? ponto.lng : null,
          };
        });
        Object.assign(envio, {
          modalidades: textoParaLista(dados.modalidadesTexto),
          categoriasPorModalidade: categorias,
          podioPorModalidade: dados.podios || {},
          locais,
          equipe: dados.equipe || [],
        });
      }

      const r = await enviarFichaDeNucleo(fichaId, token, envio);
      setEnviada(true);
      setStatus({ tipo: "ok", msg: r.mensagem || "Formulário enviado." });
      // A confirmação fica no topo, e o botão de enviar no fim de um
      // formulário comprido. Sem a rolagem, quem envia não vê a resposta.
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* segue */ }
    } catch (e) {
      setStatus({ tipo: "erro", msg: e.message || "Não foi possível enviar o formulário." });
    } finally {
      setEnviando(false);
    }
  }

  const moldura = (conteudo) => (
    <div style={{ minHeight: "100vh", background: FUNDO_SUAVE, padding: "28px 16px 48px", fontFamily: FONTE }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <img src="/logo-revelar-horizontal.png" alt="Revelar Talentos" style={{ height: 52, objectFit: "contain" }} />
        </div>
        {conteudo}
        <div style={{ textAlign: "center", fontSize: 11.5, color: CINZA_ESC, marginTop: 24, lineHeight: 1.6 }}>
          Programa Revelar Talentos. Este formulário é de uso interno do programa,
          e o link é pessoal: não repasse.
        </div>
      </div>
    </div>
  );

  if (carregando) {
    return moldura(
      <Card><div style={{ fontSize: 14, color: CINZA_ESC }}>Abrindo o formulário...</div></Card>
    );
  }

  if (erroDeAbertura) {
    return moldura(
      <Card>
        <div style={{ fontSize: 16, fontWeight: 800, color: PERIGO, marginBottom: 8 }}>
          Não foi possível abrir o formulário
        </div>
        <div style={{ fontSize: 13.5, color: TEXTO, lineHeight: 1.7 }}>{erroDeAbertura}</div>
        <div style={{ fontSize: 12.5, color: CINZA_ESC, lineHeight: 1.7, marginTop: 12 }}>
          Confira se o link foi copiado inteiro, sem cortar o fim. Se o problema
          continuar, peça um link novo à administração do programa.
        </div>
      </Card>
    );
  }

  if (enviada) {
    return moldura(
      <Card>
        <div style={{ fontSize: 17, fontWeight: 800, color: TEXTO, marginBottom: 8 }}>
          Formulário enviado
        </div>
        <div style={{ fontSize: 13.5, color: TEXTO, lineHeight: 1.7 }}>
          A administração do programa recebeu as informações de {ROTULO_DA_PARTE[ficha.parte].toLowerCase()}
          {ficha.nomeDoNucleo ? ` para ${ficha.nomeDoNucleo}` : ""}. O núcleo é cadastrado
          depois da conferência, e você não precisa fazer mais nada por aqui.
        </div>
        <div style={{ fontSize: 12.5, color: CINZA_ESC, lineHeight: 1.7, marginTop: 12 }}>
          Lembrou de algo depois de enviar? Este mesmo link continua valendo:
          abra de novo, corrija e envie outra vez.
        </div>
        <div style={{ marginTop: 16 }}>
          <Btn variant="secondary" onClick={() => { setEnviada(false); carregar(); }}>
            Abrir o formulário de novo
          </Btn>
        </div>
      </Card>
    );
  }

  return moldura(
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card>
        <div style={{ fontSize: 11.5, fontWeight: 800, color: AZUL, letterSpacing: 0.8, textTransform: "uppercase" }}>
          Ficha de cadastro de núcleo
        </div>
        <div style={{ fontSize: 19, fontWeight: 800, color: TEXTO, marginTop: 4 }}>
          {ficha.nomeDoNucleo || ficha.rotulo || "Novo núcleo"}
        </div>
        <div style={{ fontSize: 13, color: CINZA_ESC, marginTop: 6, lineHeight: 1.65 }}>
          {EXPLICACAO_DA_PARTE[ficha.parte]} A outra parte vai para o outro coordenador,
          por um link próprio, e o que você escrever aqui não é apagado por ele.
        </div>
        {ficha.recadoDoAdministrador && (
          <div style={{ background: "#EAF0FA", borderRadius: RAIO.md, padding: "11px 13px", fontSize: 13, color: TEXTO, lineHeight: 1.6, marginTop: 12 }}>
            {ficha.recadoDoAdministrador}
          </div>
        )}
        {ficha.jaEnviada && (
          <div style={{ fontSize: 12.5, color: ALERTA, lineHeight: 1.6, marginTop: 12, fontWeight: 600 }}>
            Esta parte já foi enviada uma vez. O que está preenchido abaixo é o que
            chegou, e enviar de novo substitui.
          </div>
        )}
        <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 12, lineHeight: 1.6 }}>
          Nada aqui é obrigatório. O que você não souber agora pode ficar em branco,
          e a administração do programa cobra só o que faltar.
        </div>
      </Card>

      {status && <StatusBox tipo={status.tipo} msg={status.msg} />}

      <Card>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Bloco titulo="Quem está preenchendo" descricao="Para a administração do programa saber a quem voltar em caso de dúvida.">
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Input label="Seu nome" value={dados.quemPreencheuNome || ""} onChange={(x) => mudar("quemPreencheuNome", x)}
                style={{ flex: 1, minWidth: 220 }} />
              <Input label="Seu e-mail" value={dados.quemPreencheuEmail || ""} onChange={(x) => mudar("quemPreencheuEmail", x)}
                type="email" style={{ flex: 1, minWidth: 220 }} />
            </div>
          </Bloco>

          {ficha.parte === "geral"
            ? <ParteGeral dados={dados} onMudar={mudar} />
            : <ParteTecnica dados={dados} onMudar={mudar} modalidadesInformadas={ficha.modalidadesInformadas || []} />}

          <Bloco titulo="Observações" descricao="O que não couber nos campos acima.">
            <Area value={dados.observacoes || ""} onChange={(x) => mudar("observacoes", x)} rows={4} />
          </Bloco>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap", borderTop: `1px solid ${BORDA}`, paddingTop: 16 }}>
            <Btn onClick={enviar} disabled={enviando}>
              {enviando ? "Enviando..." : "Enviar o formulário"}
            </Btn>
          </div>
        </div>
      </Card>
    </div>
  );
}
